/**
 * "Compare two browsers": parse two exported reports and diff them, entirely on this page.
 *
 * Reports may come from anywhere (a file, a paste), so everything is treated as untrusted:
 * shapes are validated, strings are coerced and length-limited, and nothing is ever rendered as HTML.
 */
import { canonicalJson, RANDOMIZED_PLACEHOLDER, sha256Hex, UNAVAILABLE_PLACEHOLDER } from './entropy/hash';

export const MAX_REPORT_BYTES = 5_000_000;
const MAX_SIGNALS = 2000;
const MAX_DISPLAY = 4000;

export interface ReportSignal {
  id: string;
  label: string;
  category: string;
  /** Canonical JSON of the raw value: what equality is judged on. */
  valueKey: string;
  display: string;
  bits: number | null;
  available: boolean;
  randomized: boolean;
}

export interface ComparableReport {
  appVersion: string;
  methodVersion: string;
  generatedAt: string;
  browser: string;
  band: string;
  bits: number | null;
  hash: string;
  /** Signal ids that feed the stable hash. */
  stableIds: Set<string>;
  signals: Map<string, ReportSignal>;
  /** Canonical JSON of the exported hash input, for re-verifying the hash. */
  hashInputJson: string;
  /** Hashed signal values agree with the hash input (false suggests the file was edited). */
  consistent: boolean;
  /** Set by verifyReportHash(): the hash recomputes from its input. */
  hashVerified?: boolean;
}

export type ParseResult = { ok: true; report: ComparableReport } | { ok: false; error: string };

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown, max = 200) => (typeof v === 'string' ? v : v === undefined || v === null ? '' : String(v)).slice(0, max);
const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null);

export function parseReport(text: string): ParseResult {
  if (text.length > MAX_REPORT_BYTES) return { ok: false, error: 'That file is too large to be a report.' };
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    return { ok: false, error: 'That is not valid JSON.' };
  }
  if (!isObject(data) || !Array.isArray(data.categories) || !isObject(data.stableHash) || !isObject(data.summary)) {
    return { ok: false, error: 'That JSON is not a Fingerprint Self-Check report (use “Download JSON” to create one).' };
  }

  const signals = new Map<string, ReportSignal>();
  for (const cat of data.categories) {
    if (!isObject(cat) || !Array.isArray(cat.signals)) continue;
    for (const s of cat.signals) {
      if (signals.size >= MAX_SIGNALS) break;
      if (!isObject(s) || typeof s.id !== 'string') continue;
      let valueKey: string;
      try {
        valueKey = canonicalJson(s.value);
      } catch {
        valueKey = str(s.display, MAX_DISPLAY);
      }
      signals.set(s.id, {
        id: str(s.id),
        label: str(s.label) || str(s.id),
        category: str(cat.title) || str(cat.id),
        valueKey,
        display: str(s.display, MAX_DISPLAY),
        bits: num(s.bits),
        available: s.available !== false,
        randomized: s.randomized === true,
      });
    }
  }
  if (!signals.size) return { ok: false, error: 'That report contains no signals.' };

  const input = data.stableHash.input;
  let consistent = isObject(input);
  if (isObject(input)) {
    for (const [id, v] of Object.entries(input)) {
      const s = signals.get(id);
      if (!s) continue;
      const expected = s.randomized ? JSON.stringify(RANDOMIZED_PLACEHOLDER) : !s.available ? JSON.stringify(UNAVAILABLE_PLACEHOLDER) : s.valueKey;
      if (canonicalJson(v) !== expected) {
        consistent = false;
        break;
      }
    }
  }
  return {
    ok: true,
    report: {
      appVersion: str(data.appVersion, 40),
      methodVersion: str(data.estimateMethodVersion, 40),
      generatedAt: str(data.generatedAt, 40),
      browser: str(data.summary.browserGuess),
      band: str(data.summary.band, 20),
      bits: num(data.summary.estimatedBits),
      hash: str(data.stableHash.hex, 128),
      stableIds: new Set(isObject(input) ? Object.keys(input) : []),
      signals,
      hashInputJson: isObject(input) ? canonicalJson(input) : '',
      consistent,
    },
  };
}

/** Recompute the hash from the exported input, as `npm run verify-report` does. */
export async function verifyReportHash(report: ComparableReport): Promise<boolean> {
  try {
    report.hashVerified = !!report.hashInputJson && (await sha256Hex(report.hashInputJson)) === report.hash;
  } catch {
    report.hashVerified = undefined; // Web Crypto unavailable: unknown, not failed
  }
  return report.hashVerified === true;
}

export interface SignalDiff {
  id: string;
  label: string;
  category: string;
  a: string | null;
  b: string | null;
  /** Part of the stable hash, so this difference changes the fingerprint hash. */
  inHash: boolean;
  /** Randomized in either report: differences are expected noise. */
  randomized: boolean;
}

export interface Comparison {
  sameHash: boolean;
  /** Both reports passed the integrity checks, so the hash verdict can be trusted. */
  trustworthy: boolean;
  /** Differences in signals that feed the stable hash. */
  hashDiffs: SignalDiff[];
  /** Differences in signals that are not part of the hash (window size, battery, …). */
  otherDiffs: SignalDiff[];
  identical: number;
  warnings: string[];
}

export function compareReports(a: ComparableReport, b: ComparableReport): Comparison {
  const warnings: string[] = [];
  if (a.methodVersion !== b.methodVersion)
    warnings.push(`The reports use different estimate methods (v${a.methodVersion || '?'} and v${b.methodVersion || '?'}), so their bit estimates aren't directly comparable.`);
  if (a.appVersion !== b.appVersion)
    warnings.push(`The reports come from different app versions (${a.appVersion || '?'} and ${b.appVersion || '?'}), which may collect slightly different signals.`);

  let trustworthy = true;
  for (const [name, r] of [
    ['A', a],
    ['B', b],
  ] as const) {
    if (r.hashVerified === false) {
      trustworthy = false;
      warnings.push(`Report ${name}'s hash does not match its own hash input, so the file was probably edited.`);
    }
    if (!r.consistent) {
      trustworthy = false;
      warnings.push(`Report ${name}'s signal values do not match its hash input, so the file was probably edited.`);
    }
  }

  const hashDiffs: SignalDiff[] = [];
  const otherDiffs: SignalDiff[] = [];
  let identical = 0;
  const ids = [...new Set([...a.signals.keys(), ...b.signals.keys()])];
  for (const id of ids) {
    const sa = a.signals.get(id);
    const sb = b.signals.get(id);
    if (sa && sb && sa.valueKey === sb.valueKey && sa.available === sb.available) {
      identical++;
      continue;
    }
    const ref = (sa ?? sb)!;
    const diff: SignalDiff = {
      id,
      label: ref.label,
      category: ref.category,
      a: sa ? sa.display : null,
      b: sb ? sb.display : null,
      inHash: a.stableIds.has(id) || b.stableIds.has(id),
      randomized: !!(sa?.randomized || sb?.randomized),
    };
    (diff.inHash ? hashDiffs : otherDiffs).push(diff);
  }
  // Stable, non-randomized differences first: those are what really tell the two browsers apart.
  hashDiffs.sort((x, y) => Number(x.randomized) - Number(y.randomized));
  return { sameHash: !!a.hash && a.hash === b.hash, trustworthy, hashDiffs, otherDiffs, identical, warnings };
}
